/**
 * The A4 draft editing surface — Phase 22, Task 22.5 (PRD 11.7).
 *
 * ═══ WHY THIS COMPONENT ONLY EVER RENDERS A DRAFT ═══
 *
 * KD-081d splits the invoice screen by status:
 *
 *   DRAFT   → edited here, on an A4-shaped surface, fields in place.
 *   ISSUED  → the REAL archived PDF (see `invoice-pdf-frame.jsx`).
 *
 * The legal document is produced server-side by `services/invoice_pdf.py`.
 * An HTML replica of an ISSUED invoice would be a SECOND RENDERER, and any
 * drift between the two — one field, one rounding rule — would mean the admin
 * reads one document on screen and sends a different one to their customer.
 * So this surface never renders a non-draft, and `InvoiceDetailPage` never
 * asks it to.
 *
 * For a DRAFT the risk does not exist in the same way: a draft has no archived
 * PDF, nothing has been sent, and the admin is here precisely to change it.
 * This is an EDITOR SHAPED LIKE THE DOCUMENT, and it is faithful about the one
 * thing that matters — field order. Top to bottom it mirrors the renderer:
 *
 *   logo → title/number + invoice date → invoice address (+ delivery)
 *   → meta columns → header_note → intro_text → LINE TABLE → notes (captioned)
 *   → totals → footer_note → the four-column seller footer band
 *
 * ═══ MONEY ═══
 *
 * `services/invoicing.py` is authoritative for every figure on a stored
 * document, and the totals block below is read straight off it — nothing here
 * sums anything.
 *
 * The two per-line money columns (`Moms kr`, `Belopp`) are the ONE place a
 * client-side figure appears, and only while the draft has unsaved edits: a
 * line the admin just typed has no server figure at all, and a blank column
 * where an amount belongs is what the admin will not notice is missing. Those
 * cells go through `previewLineMoney`, which reproduces
 * `services.invoicing.line_money` exactly — round the net per line, then the
 * VAT off the rounded net, then the sum — and they are rendered in italic grey
 * with a stated caption until the save comes back. Document TOTALS are never
 * previewed: öresavrundning is a server rule and guessing at it would put a
 * figure on screen that the PDF can disagree with.
 *
 * ═══ WHAT IS NOT ON THE SHEET ═══
 *
 * The cost centre (Task 22.4) is INTERNAL — Fortnox posting data the customer
 * never sees — so it is not rendered here at all. `InvoiceDetailPage` shows it
 * in the app chrome beside the sheet.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react';
import {
    currencySuffix,
    formatMoney,
    formatPercent,
    previewLineMoney,
    unitLabel,
} from '../pages/admin/invoice-utils';
import './invoice-a4.css';

// Same public endpoint the top bar and the Company page already use. The
// renderer resolves the tenant's logo from disk by `company_id`; this is the
// browser's route to the same file. A tenant with no logo 404s, and the sheet
// simply has no logo — exactly what the PDF does.
const LOGO_URL = `${process.env.REACT_APP_BACKEND_URL}/api/company/logo`;

/** One label/value pair in a meta column. Empty values print nothing. */
function Pair({ label, children }) {
    return (
        <>
            <span className="inv-sheet__label">{label}</span>
            <span>{children}</span>
        </>
    );
}

/** A read-only meta value; renders nothing at all when there is none. */
function ReadPair({ label, value }) {
    if (value === null || value === undefined || value === '') return null;
    return <Pair label={label}>{value}</Pair>;
}

/**
 * An editable meta field sitting in the document's own grid.
 *
 * The visible label IS the input's label (`htmlFor` / `id`) — Phase 5 finding
 * 5.133. Nothing here uses a placeholder as a substitute for one.
 */
function EditPair({ id, label, type = 'text', value, onChange }) {
    return (
        <>
            <label className="inv-sheet__label" htmlFor={id}>{label}</label>
            <input
                id={id}
                type={type}
                className="inv-field"
                value={value}
                onChange={(event) => onChange(event.target.value)}
                data-testid={id}
            />
        </>
    );
}

/**
 * One of the four admin-authored free-text blocks (PRD 10.14), edited where it
 * actually lands on the page. The label is visually hidden — the block's
 * POSITION is what identifies it on a document, and a caption over each one
 * would be four captions the PDF does not print — but it is a real `<label>`,
 * so it reaches a screen reader.
 */
function TextBlock({ id, label, value, onChange, placeholder }) {
    const rows = Math.max(2, String(value || '').split('\n').length);
    return (
        <>
            <label className="sr-only" htmlFor={id}>{label}</label>
            <textarea
                id={id}
                className="inv-field inv-field--block"
                rows={rows}
                maxLength={4000}
                value={value}
                placeholder={placeholder}
                onChange={(event) => onChange(event.target.value)}
                data-testid={id}
            />
        </>
    );
}

export default function InvoiceA4Sheet({
    invoice,
    form,
    lines,
    lineErrors,
    dirty,
    currency,
    unitOptions,
    totalsPreview,
    onFormField,
    onLineField,
    onAddLine,
    onRemoveLine,
    onMoveLine,
}) {
    const { t } = useTranslation();
    const [logoFailed, setLogoFailed] = React.useState(false);

    const client = invoice.client_snapshot || {};
    // `company_snapshot` is frozen at ISSUE and deliberately empty on a draft
    // (see the module docstring). This surface never renders anything but a
    // draft, so `company_preview` — the seller block as it would resolve
    // RIGHT NOW (Task 23.9, PRD 12.9) — is always the one to prefer; the
    // snapshot is kept only as a defensive fallback should a caller ever
    // hand this component an invoice built without it.
    const company = invoice.company_preview || invoice.company_snapshot || {};
    const billing = client.billing_address || {};
    const delivery = client.delivery_address || {};

    const isCreditNote = invoice.doc_type === 'credit_note';
    const title = isCreditNote
        ? t('invoices.doc_type_credit_note')
        : t('invoices.doc_type_invoice');
    const numberLabel = isCreditNote
        ? t('invoices.sheet_num_credit_note')
        : t('invoices.sheet_num_invoice');

    // The `Moms kr` header is a FACTUAL CLAIM about the figures beneath it, so
    // it carries the invoice's own currency — "Moms kr" for kronor, "Moms EUR"
    // for euros — exactly as `_vat_amount_header` does in the renderer. A
    // document with no currency set falls back to currency-free wording rather
    // than borrowing the krona's symbol.
    const suffix = currencySuffix(currency);
    const vatAmountHeader = suffix
        ? t('invoices.sheet_col_vat_amount', { currency: suffix })
        : t('invoices.sheet_col_vat_amount_plain');

    const postal = [billing.postal_code, billing.city].filter(Boolean).join(' ');
    const deliveryPostal = [delivery.postal_code, delivery.city].filter(Boolean).join(' ');
    const showDelivery = client.delivery_same_as_billing === false
        && Boolean(delivery.street || deliveryPostal || delivery.country);

    // The four seller footer columns, mirroring `_footer_columns`: whichever
    // payment identifier the company actually carries becomes the prominent
    // one, and an empty column prints nothing rather than a heading over
    // nothing.
    const identifiers = [
        [t('invoices.meta_bankgiro'), company.bankgiro],
        [t('invoices.meta_plusgiro'), company.plusgiro],
        [t('invoices.meta_iban'), company.iban],
        [t('invoices.meta_bic'), company.bic],
    ].filter(([, value]) => Boolean(value));

    const contactLines = [company.phone, company.email, company.website].filter(Boolean);
    const addressLines = [company.name, ...String(company.legal_address || '').split('\n')]
        .map((line) => (line || '').trim())
        .filter(Boolean);

    const bands = invoice.vat_by_rate || [];

    /**
     * The live document total (Task 23.9, PRD 12.8).
     *
     * `totalsPreview` is owned by `InvoiceDetailPage`, which debounces a call
     * to `POST /invoices/{id}/price-preview` — the REAL engine, priced over
     * the unsaved lines — and never guesses at öresavrundning itself (see
     * the module docstring's MONEY section).
     *
     * `preview` is used ONLY while the draft is dirty, and only once the
     * server has actually priced the current lines: `status === 'loading'`
     * keeps whatever the previous successful preview was on screen (so one
     * more keystroke never blanks a figure already shown), and
     * `status === 'error'` — the lines do not parse locally, or the request
     * failed — falls all the way back to exactly what Task 22.5 shipped: the
     * last SAVED figures, captioned stale. A stale preview is never shown as
     * if it were current.
     */
    const preview = dirty ? totalsPreview?.data : null;
    const totalsSource = preview || invoice;
    const totalsCurrency = preview ? preview.currency : currency;
    const totalsBands = preview ? (preview.vat_by_rate || []) : bands;
    const showingPreviewTotals = Boolean(preview);
    const totalsValueClass = showingPreviewTotals ? 'inv-num inv-preview' : 'inv-num';

    return (
        <div className="inv-stage">
            <article className="inv-sheet" data-testid="invoice-a4-sheet">
                <div className="inv-sheet__body">
                    {!logoFailed && (
                        <img
                            src={LOGO_URL}
                            alt={t('invoices.sheet_logo_alt')}
                            className="inv-sheet__logo"
                            onError={() => setLogoFailed(true)}
                        />
                    )}

                    {/* Title + number | invoice date */}
                    <div className="inv-sheet__cols">
                        <div>
                            <h2 className="inv-sheet__title">{title}</h2>
                            <p className="inv-sheet__number">
                                {numberLabel}{' '}
                                {invoice.invoice_number || t('invoices.sheet_draft_stamp')}
                            </p>
                        </div>
                        <div className="inv-sheet__pairs">
                            <EditPair
                                id="sheet-invoice-date"
                                label={t('invoices.meta_invoice_date')}
                                type="date"
                                value={form.invoice_date}
                                onChange={(value) => onFormField('invoice_date', value)}
                            />
                        </div>
                    </div>

                    {/* Invoice address (+ delivery address only when it differs) */}
                    <div className="inv-sheet__section">
                        <p className="inv-sheet__label">{t('invoices.section_bill_to')}</p>
                        <p className="inv-sheet__strong">{client.name || '—'}</p>
                        {billing.street && <p>{billing.street}</p>}
                        {postal && <p>{postal}</p>}
                        {billing.country && <p>{billing.country}</p>}

                        {showDelivery && (
                            <div style={{ marginTop: '3mm' }}>
                                <p className="inv-sheet__label">{t('invoices.section_deliver_to')}</p>
                                {delivery.street && <p>{delivery.street}</p>}
                                {deliveryPostal && <p>{deliveryPostal}</p>}
                                {delivery.country && <p>{delivery.country}</p>}
                            </div>
                        )}
                    </div>

                    {/* The two meta columns, in the renderer's arrangement. */}
                    <div className="inv-sheet__section inv-sheet__cols">
                        <div className="inv-sheet__pairs">
                            <ReadPair
                                label={t('invoices.meta_client_number')}
                                value={client.client_number}
                            />
                            <EditPair
                                id="sheet-reference-no"
                                label={t('invoices.meta_reference_no')}
                                value={form.reference_no}
                                onChange={(value) => onFormField('reference_no', value)}
                            />
                            <EditPair
                                id="sheet-their-reference"
                                label={t('invoices.meta_their_reference')}
                                value={form.their_reference}
                                onChange={(value) => onFormField('their_reference', value)}
                            />
                            <EditPair
                                id="sheet-your-order-no"
                                label={t('invoices.meta_your_order_no')}
                                value={form.your_order_no}
                                onChange={(value) => onFormField('your_order_no', value)}
                            />
                            <ReadPair
                                label={t('invoices.meta_vat_number')}
                                value={client.vat_number}
                            />
                        </div>
                        <div className="inv-sheet__pairs">
                            <EditPair
                                id="sheet-our-reference"
                                label={t('invoices.meta_our_reference')}
                                value={form.our_reference}
                                onChange={(value) => onFormField('our_reference', value)}
                            />
                            <ReadPair
                                label={t('invoices.meta_payment_terms')}
                                value={client.payment_terms_days != null
                                    ? t('invoices.payment_terms_value', {
                                        days: client.payment_terms_days,
                                    })
                                    : ''}
                            />
                            <EditPair
                                id="sheet-due-date"
                                label={t('invoices.meta_due_date')}
                                type="date"
                                value={form.due_date}
                                onChange={(value) => onFormField('due_date', value)}
                            />
                            <ReadPair label={t('invoices.meta_ocr')} value={invoice.ocr_reference} />
                            <ReadPair
                                label={t('invoices.meta_overdue_interest')}
                                value={client.overdue_interest_pct != null
                                    ? formatPercent(client.overdue_interest_pct)
                                    : ''}
                            />
                            <EditPair
                                id="sheet-delivery-date"
                                label={t('invoices.meta_delivery_date')}
                                type="date"
                                value={form.delivery_date}
                                onChange={(value) => onFormField('delivery_date', value)}
                            />
                        </div>
                    </div>

                    {/* 1 — header_note: under the meta area, above the line region. */}
                    <div className="inv-sheet__section">
                        <TextBlock
                            id="sheet-header-note"
                            label={t('invoices.sheet_block_header_note')}
                            value={form.header_note}
                            onChange={(value) => onFormField('header_note', value)}
                            placeholder={t('invoices.sheet_block_placeholder')}
                        />
                    </div>

                    {/* 2 — intro_text: immediately above the line table. */}
                    <div style={{ marginTop: '4mm' }}>
                        <TextBlock
                            id="sheet-intro-text"
                            label={t('invoices.sheet_block_intro_text')}
                            value={form.intro_text}
                            onChange={(value) => onFormField('intro_text', value)}
                            placeholder={t('invoices.sheet_block_placeholder')}
                        />
                    </div>

                    {/* The seven printed columns, plus a chrome gutter for the
                        row controls. The gutter is EDITING apparatus, not
                        document content — it has no printed counterpart and is
                        styled to read as chrome.

                        Proportions mirror `numeric_w` in `_lines_table`
                        (`services/invoice_pdf.py`) by contract (Task 23.10,
                        PRD 12.10) — the two are the same seven columns and
                        must be rebalanced together or the editor lies about
                        the paper. Quantity gives up room (typical content:
                        "7,4", "41,5") and Belopp gives up a little of its
                        own generous share, to à pris, Moms and Moms kr — the
                        three the owner named as colliding. Unit is left
                        alone: a tenant's own free-text unit already sits
                        close to this column's minimum, and the PDF side of
                        this same change proved that shrinking it further
                        drops the text rather than just looking tight.
                        Beskrivning absorbs whatever is left. */}
                    <div style={{ marginTop: '5mm' }}>
                        <table className="inv-sheet__lines">
                            <colgroup>
                                <col />
                                <col style={{ width: '6%' }} />
                                <col style={{ width: '7%' }} />
                                <col style={{ width: '16%' }} />
                                <col style={{ width: '9%' }} />
                                <col style={{ width: '16%' }} />
                                <col style={{ width: '15%' }} />
                                <col style={{ width: '74px' }} />
                            </colgroup>
                            <thead>
                                <tr>
                                    <th scope="col">{t('invoices.col_designation')}</th>
                                    <th scope="col" className="inv-num">{t('invoices.col_quantity')}</th>
                                    <th scope="col">{t('invoices.col_unit')}</th>
                                    <th scope="col" className="inv-num">{t('invoices.col_price')}</th>
                                    <th scope="col" className="inv-num">{t('invoices.col_vat_rate')}</th>
                                    <th scope="col" className="inv-num">{vatAmountHeader}</th>
                                    <th scope="col" className="inv-num">{t('invoices.col_line_total')}</th>
                                    <th scope="col" className="inv-sheet__gutter">
                                        <span className="sr-only">{t('invoices.sheet_row_actions')}</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {lines.map((line, index) => {
                                    const saved = (invoice.lines || [])[index] || null;
                                    // While the draft is clean the saved figures
                                    // ARE this row's figures. Once anything is
                                    // edited the row/save correspondence can no
                                    // longer be trusted (a reorder or a delete
                                    // shifts every index), so the whole money
                                    // region switches to the marked preview.
                                    const preview = dirty
                                        ? previewLineMoney(line, invoice.vat_treatment)
                                        : null;
                                    const vatAmount = dirty ? preview?.vat : saved?.vat_amount;
                                    const lineTotal = dirty ? preview?.total : saved?.line_total_incl_vat;
                                    const moneyClass = dirty ? 'inv-num inv-preview' : 'inv-num';
                                    const rowErrors = lineErrors[index] || {};

                                    return (
                                        <tr key={index} data-testid={`sheet-line-${index}`}>
                                            <td>
                                                <label className="sr-only" htmlFor={`sheet-line-${index}-designation`}>
                                                    {t('invoices.col_designation')}
                                                </label>
                                                <input
                                                    id={`sheet-line-${index}-designation`}
                                                    className={`inv-field${rowErrors.designation ? ' inv-field--error' : ''}`}
                                                    value={line.designation}
                                                    onChange={(e) => onLineField(index, 'designation', e.target.value)}
                                                    data-testid={`sheet-line-${index}-designation`}
                                                />
                                                {rowErrors.designation && (
                                                    <p className="inv-sheet__field-error">{rowErrors.designation}</p>
                                                )}
                                            </td>
                                            <td>
                                                <label className="sr-only" htmlFor={`sheet-line-${index}-quantity`}>
                                                    {t('invoices.col_quantity')}
                                                </label>
                                                <input
                                                    id={`sheet-line-${index}-quantity`}
                                                    inputMode="decimal"
                                                    className={`inv-field inv-field--right${rowErrors.quantity ? ' inv-field--error' : ''}`}
                                                    value={line.quantity}
                                                    onChange={(e) => onLineField(index, 'quantity', e.target.value)}
                                                    data-testid={`sheet-line-${index}-quantity`}
                                                />
                                                {rowErrors.quantity && (
                                                    <p className="inv-sheet__field-error">{rowErrors.quantity}</p>
                                                )}
                                            </td>
                                            <td>
                                                <label className="sr-only" htmlFor={`sheet-line-${index}-unit`}>
                                                    {t('invoices.col_unit')}
                                                </label>
                                                <select
                                                    id={`sheet-line-${index}-unit`}
                                                    className="inv-field"
                                                    value={line.unit || ''}
                                                    onChange={(e) => onLineField(index, 'unit', e.target.value)}
                                                    data-testid={`sheet-line-${index}-unit`}
                                                >
                                                    {/* An empty unit is legitimate and must survive a
                                                        save untouched, so it is an option rather than
                                                        something the select silently replaces. */}
                                                    <option value="">—</option>
                                                    {unitOptions.map((unit) => (
                                                        <option key={unit} value={unit}>{unitLabel(unit)}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td>
                                                <label className="sr-only" htmlFor={`sheet-line-${index}-price`}>
                                                    {t('invoices.col_price')}
                                                </label>
                                                <input
                                                    id={`sheet-line-${index}-price`}
                                                    inputMode="decimal"
                                                    className={`inv-field inv-field--right${rowErrors.price_per_unit ? ' inv-field--error' : ''}`}
                                                    value={line.price_per_unit}
                                                    onChange={(e) => onLineField(index, 'price_per_unit', e.target.value)}
                                                    data-testid={`sheet-line-${index}-price`}
                                                />
                                                {rowErrors.price_per_unit && (
                                                    <p className="inv-sheet__field-error">{rowErrors.price_per_unit}</p>
                                                )}
                                            </td>
                                            <td>
                                                <label className="sr-only" htmlFor={`sheet-line-${index}-vat`}>
                                                    {t('invoices.col_vat_rate')}
                                                </label>
                                                <input
                                                    id={`sheet-line-${index}-vat`}
                                                    inputMode="decimal"
                                                    className={`inv-field inv-field--right${rowErrors.vat_rate ? ' inv-field--error' : ''}`}
                                                    value={line.vat_rate}
                                                    onChange={(e) => onLineField(index, 'vat_rate', e.target.value)}
                                                    data-testid={`sheet-line-${index}-vat`}
                                                />
                                                {rowErrors.vat_rate && (
                                                    <p className="inv-sheet__field-error">{rowErrors.vat_rate}</p>
                                                )}
                                            </td>
                                            <td className={moneyClass} data-testid={`sheet-line-${index}-vat-amount`}>
                                                {vatAmount == null ? '' : formatMoney(vatAmount, currency)}
                                            </td>
                                            <td className={moneyClass} data-testid={`sheet-line-${index}-total`}>
                                                {lineTotal == null ? '' : formatMoney(lineTotal, currency)}
                                            </td>
                                            <td className="inv-sheet__gutter">
                                                <button
                                                    type="button"
                                                    className="inv-sheet__gutter-btn"
                                                    onClick={() => onMoveLine(index, -1)}
                                                    disabled={index === 0}
                                                    aria-label={t('invoices.move_line_up')}
                                                >
                                                    <ChevronUp className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="inv-sheet__gutter-btn"
                                                    onClick={() => onMoveLine(index, 1)}
                                                    disabled={index === lines.length - 1}
                                                    aria-label={t('invoices.move_line_down')}
                                                >
                                                    <ChevronDown className="w-4 h-4" />
                                                </button>
                                                <button
                                                    type="button"
                                                    className="inv-sheet__gutter-btn"
                                                    onClick={() => onRemoveLine(index)}
                                                    aria-label={t('invoices.remove_line')}
                                                    data-testid={`sheet-remove-line-${index}`}
                                                >
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>

                        <button
                            type="button"
                            className="inv-sheet__add-line"
                            onClick={onAddLine}
                            data-testid="sheet-add-line-btn"
                        >
                            <Plus className="w-4 h-4" />
                            {t('invoices.add_line')}
                        </button>

                        {dirty && (
                            <p
                                className="inv-sheet__muted"
                                style={{ marginTop: '2mm', fontSize: '10px' }}
                                data-testid="sheet-preview-hint"
                            >
                                {t('invoices.sheet_preview_hint')}
                            </p>
                        )}
                    </div>

                    {/* 3 — notes: after the line table, before the totals. The
                        ONLY captioned block, exactly as the renderer prints it. */}
                    <div style={{ marginTop: '5mm' }}>
                        <p className="inv-sheet__strong">{t('invoices.sheet_block_notes')}</p>
                        <TextBlock
                            id="sheet-notes"
                            label={t('invoices.sheet_block_notes')}
                            value={form.notes}
                            onChange={(value) => onFormField('notes', value)}
                            placeholder={t('invoices.sheet_block_placeholder')}
                        />
                    </div>

                    {/* Totals. Nothing here is ever computed in the browser —
                        see `preview` above. Clean draft: the saved figures,
                        no caption. Dirty with a priced preview: the SERVER's
                        figures for the lines as they stand right now, in the
                        same marked-preview treatment the per-line columns
                        use. Dirty with no usable preview yet (loading with
                        nothing cached, or an error): the last saved figures,
                        captioned stale — exactly Task 22.5's behaviour. */}
                    <div className="inv-sheet__totals" data-testid="sheet-totals">
                        <div className="inv-sheet__total-row">
                            <span className="inv-sheet__label">{t('invoices.total_excl_vat')}</span>
                            <span className={totalsValueClass}>
                                {formatMoney(totalsSource.total_excl_vat, totalsCurrency)}
                            </span>
                        </div>
                        {totalsBands.length > 1 && totalsBands.map((band) => (
                            <div className="inv-sheet__total-row" key={band.vat_rate}>
                                <span className="inv-sheet__label">
                                    {t('invoices.vat_at_rate', { rate: formatPercent(band.vat_rate) })}
                                </span>
                                <span className={totalsValueClass}>{formatMoney(band.vat, totalsCurrency)}</span>
                            </div>
                        ))}
                        <div className="inv-sheet__total-row">
                            <span className="inv-sheet__label">{t('invoices.vat_total')}</span>
                            <span className={totalsValueClass}>{formatMoney(totalsSource.vat_total, totalsCurrency)}</span>
                        </div>
                        <div className="inv-sheet__total-row">
                            <span className="inv-sheet__label">{t('invoices.rounding')}</span>
                            <span className={totalsValueClass}>{formatMoney(totalsSource.rounding, totalsCurrency)}</span>
                        </div>
                        <div className="inv-sheet__total-row inv-sheet__total-row--final">
                            <span>{t('invoices.total_incl_vat')}</span>
                            <span className={totalsValueClass} data-testid="sheet-total">
                                {formatMoney(totalsSource.total_incl_vat, totalsCurrency)}
                            </span>
                        </div>
                        {showingPreviewTotals ? (
                            <p
                                className="inv-sheet__muted"
                                style={{ marginTop: '2mm', fontSize: '10px' }}
                                data-testid="sheet-totals-preview-hint"
                            >
                                {t('invoices.sheet_totals_preview_hint')}
                            </p>
                        ) : dirty && (
                            <p
                                className="inv-sheet__muted"
                                style={{ marginTop: '2mm', fontSize: '10px' }}
                                data-testid="sheet-totals-stale"
                            >
                                {t('invoices.totals_recalculated_on_save')}
                            </p>
                        )}
                    </div>

                    {/* 4 — footer_note: after the totals, above the company band. */}
                    <div style={{ marginTop: '6mm' }}>
                        <TextBlock
                            id="sheet-footer-note"
                            label={t('invoices.sheet_block_footer_note')}
                            value={form.footer_note}
                            onChange={(value) => onFormField('footer_note', value)}
                            placeholder={t('invoices.sheet_block_placeholder')}
                        />
                    </div>

                    {/* Company-level footer text (PRD 10.3), snapshotted at
                        issue and therefore READ-ONLY here — it is a company
                        setting, not per-invoice text, and the four editable
                        blocks above are where per-invoice wording belongs. */}
                    {company.invoice_footer_text && (
                        <p
                            className="inv-sheet__muted"
                            style={{ marginTop: '6mm', fontSize: '10px', whiteSpace: 'pre-wrap' }}
                        >
                            {company.invoice_footer_text}
                        </p>
                    )}
                </div>

                {/* The seller footer band. On a draft `company_preview` is
                    resolved from the LIVE company profile (Task 23.9, PRD
                    12.9) — the frozen `company_snapshot` above is only taken
                    at ISSUE, so an unfilled profile can still legitimately be
                    empty here. Saying so beats a band of blanks that reads as
                    missing data; a filled-in one is captioned as live so it
                    is never mistaken for the frozen snapshot the archived PDF
                    will actually carry. */}
                {addressLines.length === 0 && contactLines.length === 0 && identifiers.length === 0 ? (
                    <p
                        className="inv-sheet__muted"
                        style={{ marginTop: '8mm', paddingTop: '3mm', borderTop: '1px solid #999999', fontSize: '10px' }}
                    >
                        {t('invoices.company_snapshot_at_issue')}
                    </p>
                ) : (
                    <div className="inv-sheet__footer-band" data-testid="sheet-footer-band-live">
                        <p
                            className="inv-sheet__muted"
                            style={{ gridColumn: '1 / -1', fontSize: '10px', marginBottom: '2mm' }}
                            data-testid="sheet-footer-band-live-caption"
                        >
                            {t('invoices.company_preview_live_notice')}
                        </p>
                        <div>
                            {addressLines.length > 0 && (
                                <p className="inv-sheet__footer-head">{t('invoices.sheet_footer_address')}</p>
                            )}
                            {addressLines.map((line, index) => <p key={index}>{line}</p>)}
                        </div>
                        <div>
                            {contactLines.length > 0 && (
                                <p className="inv-sheet__footer-head">{t('invoices.sheet_footer_contact')}</p>
                            )}
                            {contactLines.map((line, index) => <p key={index}>{line}</p>)}
                        </div>
                        <div>
                            {identifiers.length > 0 && (
                                <>
                                    <p className="inv-sheet__footer-head">{identifiers[0][0]}</p>
                                    <p className="inv-sheet__footer-strong">{identifiers[0][1]}</p>
                                    {identifiers.slice(1).map(([label, value]) => (
                                        <p key={label}>{label}: {value}</p>
                                    ))}
                                </>
                            )}
                            {company.bank_name && identifiers.length > 0 && <p>{company.bank_name}</p>}
                        </div>
                        <div>
                            {company.org_number && (
                                <>
                                    <p className="inv-sheet__footer-head">{t('invoices.meta_org_number')}</p>
                                    <p>{company.org_number}</p>
                                </>
                            )}
                            {company.vat_number && (
                                <p>{t('invoices.meta_vat_number')}: {company.vat_number}</p>
                            )}
                            {/* Printed only when the tenant IS approved — the
                                renderer's rule. Printing the negative would
                                stamp a damaging claim about the seller onto
                                every invoice of a half-filled profile. */}
                            {company.approved_for_f_tax && <p>{t('invoices.meta_f_tax')}</p>}
                        </div>
                    </div>
                )}
            </article>
        </div>
    );
}
